import { useCallback } from "react";

import {
    IApiAdvertisement,
    IApiBackground,
    IApiFestivalSetting,
    IApiGreeting,
    IApiGreetingsWithToggle,
    IApiInteraction,
    IApiLayoutMode,
    IApiPhotoSettings,
    IApiQRCode,
    IApiSubtitleSpeed,
    IApiTimeAlerts,
    IApiVolume,
    IApiWeather,
    IKioskSettingNative,
    IMarquee,
    IScheduleMarqueeNew,
    isNewMarqueeFormat,
    IUpdateKioskReq,
} from "../../types";
import { useGetKioskSettingQuery, useUpdateKioskSettingMutation } from "../services/kioskApiSlice";
import { convertScheduleMarqueeToLegacy } from "../utils/convertMarqueeData";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import { syncHintsWithLanguages } from "../utils/syncHintsWithLanguages";

const useKioskSettings = ({ characterId }: { characterId: string }) => {
    const { data: kioskSetting, isLoading: isLoadingKioskSetting } = useGetKioskSettingQuery({
        character_id: characterId as string,
    });
    const [updateKioskSetting, { isLoading: isUpdatingKioskSetting }] = useUpdateKioskSettingMutation();

    /**
     * 基礎更新函數
     * 處理 marquee 和 native 的更新邏輯
     */
    const updateSettings = useCallback(
        async (updates: Partial<Pick<IUpdateKioskReq, "marquee" | "native">>) => {
            const nativeData = updates.native !== undefined ? updates.native : kioskSetting?.data?.native;

            let marqueeData = updates.marquee !== undefined ? updates.marquee : kioskSetting?.data?.marquee;

            // 如果沒有明確更新 marquee，但有 schedule_marquee，則轉換為舊格式
            if (updates.marquee === undefined && nativeData?.schedule_marquee) {
                const scheduleMarquee = nativeData.schedule_marquee;
                if (isNewMarqueeFormat(scheduleMarquee)) {
                    marqueeData = convertScheduleMarqueeToLegacy(scheduleMarquee);
                }
            }

            const finalData: IUpdateKioskReq = {
                character_id: characterId,
                marquee: marqueeData,
                native: nativeData,
            };

            return await updateKioskSetting(finalData).unwrap();
        },
        [characterId, kioskSetting, updateKioskSetting]
    );

    /**
     * 更新跑馬燈設定
     */
    const updateMarquee = useCallback(
        async (marqueeData: IMarquee) => {
            return await updateSettings({ marquee: marqueeData });
        },
        [updateSettings]
    );

    /**
     * 更新排程跑馬燈設定
     */
    const updateScheduleMarquee = useCallback(
        async (scheduleMarqueeData: IScheduleMarqueeNew) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                schedule_marquee: scheduleMarqueeData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新招呼詞設定
     */
    const updateGreetings = useCallback(
        async (greetingsData: IApiGreeting[] | IApiGreetingsWithToggle) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                greetings: greetingsData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新語言設定
     * 會自動同步 hints 資料
     */
    const updateLanguage = useCallback(
        async (languageUpdates: Partial<IKioskSettingNative["general"]>) => {
            const currentNative = kioskSetting?.data?.native;
            const newLanguageOptions = languageUpdates.languageOptions || currentNative?.general?.languageOptions || [];

            // 同步 hints 資料
            const syncedHints = syncHintsWithLanguages(currentNative?.hints, newLanguageOptions);

            const mergedNative = mergeKioskSettings(currentNative, {
                general: {
                    defaultLanguage: currentNative?.general?.defaultLanguage || "",
                    languageOptions: currentNative?.general?.languageOptions || [],
                    languageDisplayMode: currentNative?.general?.languageDisplayMode || "dropdown",
                    ...languageUpdates,
                },
                hints: syncedHints,
            });

            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新廣告設定
     */
    const updateAdvertisement = useCallback(
        async (advertisementData: IApiAdvertisement) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                ad: advertisementData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新拍照設定
     */
    const updatePhoto = useCallback(
        async (photoData: IApiPhotoSettings) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                photo: photoData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新背景設定
     */
    const updateBackground = useCallback(
        async (backgroundData: IApiBackground) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                background: backgroundData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新 QR Code 設定
     */
    const updateQRCode = useCallback(
        async (qrcodeData: IApiQRCode) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                QRcode: qrcodeData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新音量設定
     */
    const updateVolume = useCallback(
        async (volumeData: IApiVolume) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                volume: volumeData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新節慶設定
     */
    const updateFestival = useCallback(
        async (festivalData: IApiFestivalSetting) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                festival_setting: festivalData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新天氣設定
     */
    const updateWeather = useCallback(
        async (weatherData: IApiWeather) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                weather: weatherData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新互動設定
     */
    const updateInteraction = useCallback(
        async (interactionData: IApiInteraction) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                interaction: interactionData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新切版模式設定
     */
    const updateLayoutMode = useCallback(
        async (layoutModeData: IApiLayoutMode) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                layout_mode: layoutModeData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新字幕滾動速度設定
     */
    const updateSubtitleSpeed = useCallback(
        async (subtitleSpeedData: IApiSubtitleSpeed[]) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                subtitle_speed: subtitleSpeedData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    /**
     * 更新時間提醒設定
     */
    const updateTimeAlerts = useCallback(
        async (timeAlertsData: IApiTimeAlerts) => {
            const currentNative = kioskSetting?.data?.native;
            const mergedNative = mergeKioskSettings(currentNative, {
                time_alerts: timeAlertsData,
            });
            return await updateSettings({ native: mergedNative });
        },
        [kioskSetting?.data?.native, updateSettings]
    );

    return {
        // Data
        kioskSetting,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,

        // Update functions
        updateSettings,
        updateMarquee,
        updateScheduleMarquee,
        updateGreetings,
        updateLanguage,
        updateAdvertisement,
        updatePhoto,
        updateBackground,
        updateQRCode,
        updateVolume,
        updateFestival,
        updateWeather,
        updateTimeAlerts,
        updateInteraction,
        updateLayoutMode,
        updateSubtitleSpeed,
    };
};

export default useKioskSettings;
